/**
 * @fileoverview Raw backend response shapes and pure adapter functions for the
 * student "My tasks" module. Adapters are pure and defensive (no throw on
 * missing fields); the service/source/hooks own error handling.
 */

import type { ClassroomAttachment } from 'components/ClassroomDeliverable'
import type { DeliverableDetail, DeliveryLink, ScoreItem, StudentTask, StudentTaskType, TaskFile } from '../types'

// ─── Raw shapes (local to the service layer) ─────────────────────────────
export interface RawPendency {
  id?: string
  kind?: string
  identifierId?: string
  identifierName?: string
  status?: string
  deadline?: string | null
  overdue?: boolean
  sessionMeeting?: { date?: string }
}
export interface RawFile {
  id: string
  name: string
  fileType: string
  downloadUrl: string
}
export interface RawLink {
  id: string
  url: string
  createdAt: string
}
export interface RawScore {
  id?: string
  studentScore?: number
  name?: string
  lv1Name?: string
  color?: string
  rubrics?: Record<string, string>
  lv4Item?: { name?: string; lv1Name?: string; color?: string }
  howToAssess1?: string; howToAssess2?: string; howToAssess3?: string
  howToAssess4?: string; howToAssess5?: string
}

const KIND_TO_TYPE: Record<string, StudentTaskType> = {
  'pendencies/missing_student_task': 'deliverable',
  // Backend emits the student's self-assessment pendency with the journal kind
  // (CreateStudentMeetingPendency, profile 'student'); the assessment kind is
  // educator-only today but kept mapped for the AR-760 generated pendencies.
  'pendencies/missing_meeting_journal': 'self-assessment',
  'pendencies/missing_meeting_assessment': 'self-assessment',
  'pendencies/missing_final_report_and_evaluation': 'final-self-assessment',
}

export const DONE_STATUSES = new Set(['done', 'review', 'file'])
export const str = (value: unknown): string => (value == null ? '' : String(value))

export const mapFiles = (arr: unknown): TaskFile[] =>
  (Array.isArray(arr) ? arr : []).map((f: Partial<RawFile>) => ({
    id: str(f?.id), name: str(f?.name), fileType: str(f?.fileType), downloadUrl: str(f?.downloadUrl),
  }))

export const mapLinks = (arr: unknown): DeliveryLink[] =>
  (Array.isArray(arr) ? arr : []).map((l: Partial<RawLink>) => ({
    id: str(l?.id), url: str(l?.url), createdAt: str(l?.createdAt),
  }))

export const mapDriveFiles = (arr: unknown): ClassroomAttachment[] =>
  (Array.isArray(arr) ? arr : []).map((f: Partial<ClassroomAttachment>) => ({
    id: str(f?.id), name: str(f?.name), url: str(f?.url), iconUrl: f?.iconUrl || null,
  }))

/** Maps a raw pendency to a spine task; returns null for out-of-scope kinds. */
export function adaptPendency(raw: RawPendency): StudentTask | null {
  const type = raw.kind ? KIND_TO_TYPE[raw.kind] : undefined
  if (!type || !raw.identifierId) return null
  // The pendency spine already carries the PROJECT name (identifier_name) for
  // every kind and the lesson date (session_meeting.date) — use them as the
  // card's immediate labels so it never renders "—" while (or if) the slow
  // per-card hydration is still pending / timed out (AR-543). Hydration then
  // overwrites these with the full detail (adds the lesson title).
  const isFinal = type === 'final-self-assessment'
  return {
    id: str(raw.id) || `${type}-${raw.identifierId}`,
    type,
    identifierId: str(raw.identifierId),
    done: DONE_STATUSES.has(str(raw.status)),
    overdue: Boolean(raw.overdue),
    deadline: raw.deadline ?? null,
    projectId: isFinal ? str(raw.identifierId) : null,
    projectName: raw.identifierName ?? null,
    lessonTitle: null,
    lessonDate: isFinal ? null : (raw.sessionMeeting?.date ?? null),
    title: isFinal ? null : (raw.identifierName ?? null),
    // The spine has no delivery signal; hydration derives it (AR-873).
    inProgress: false,
  }
}

/**
 * A not-yet-submitted delivery counts as started when the student already
 * attached content to it (AR-873). Only links and Classroom Drive files can
 * exist on an OPEN delivery: description and file uploads submit on save
 * (AR-738/AR-741), and the open delivery's draft description is not
 * serialized by the show endpoint anyway.
 */
export const isDeliveryInProgress = (
  detail: Pick<DeliverableDetail, 'done' | 'links' | 'googleDriveFiles'>,
): boolean => !detail.done && (detail.links.length > 0 || detail.googleDriveFiles.length > 0)

const rubricsFromHowTo = (raw: RawScore): Record<string, string> => ({
  1: str(raw.howToAssess1), 2: str(raw.howToAssess2), 3: str(raw.howToAssess3),
  4: str(raw.howToAssess4), 5: str(raw.howToAssess5),
})

export const adaptScore = (raw: RawScore): ScoreItem => ({
  id: str(raw.id),
  name: str(raw.name || raw.lv4Item?.name),
  lv1Name: str(raw.lv1Name || raw.lv4Item?.lv1Name),
  color: str(raw.color || raw.lv4Item?.color) || '#868e96',
  studentScore: Number(raw.studentScore) || 0,
  rubrics: raw.rubrics ?? rubricsFromHowTo(raw),
})
